import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './ThirdParties.css';

const ThirdParties = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedThirdParty, setSelectedThirdParty] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [formData, setFormData] = useState({
    nit: '',
    name: '',
    type: 'cliente',
    address: '',
    city: '',
    department: '',
    phone: '',
    email: ''
  });

  // Obtener terceros
  const { data, isLoading, error } = useQuery({
    queryKey: ['thirdParties', companyId, searchTerm, filterType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterType) params.append('type', filterType);
      const response = await api.get(`/companies/${companyId}/third-parties?${params}`);
      return response.data.data;
    },
    enabled: !!companyId
  });

  // Crear tercero
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/companies/${companyId}/third-parties`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['thirdParties', companyId]);
      setShowForm(false);
      setFormData({
        nit: '',
        name: '',
        type: 'cliente',
        address: '',
        city: '',
        department: '',
        phone: '',
        email: ''
      });
    }
  });

  // Validar NIT
  const validateNITMutation = useMutation({
    mutationFn: async (thirdPartyId) => {
      const response = await api.post(`/companies/${companyId}/third-parties/${thirdPartyId}/validate-nit`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['thirdParties', companyId]);
      alert('NIT validado exitosamente');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleValidateNIT = (id) => {
    if (window.confirm('¿Deseas validar este NIT con la DIAN?')) {
      validateNITMutation.mutate(id);
    }
  };

  if (isLoading) return <div>Cargando terceros...</div>;
  if (error) return <div>Error al cargar terceros</div>;

  return (
    <div className="third-parties">
      <div className="page-header">
        <h2>Terceros</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Nuevo Tercero'}
        </button>
      </div>

      {/* Filtros y búsqueda */}
      <div className="filters-section">
        <input
          type="text"
          placeholder="Buscar por nombre, NIT o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="filter-select"
        >
          <option value="">Todos los tipos</option>
          <option value="cliente">Clientes</option>
          <option value="proveedor">Proveedores</option>
          <option value="empleado">Empleados</option>
          <option value="otro">Otros</option>
        </select>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Nuevo Tercero</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>NIT *</label>
                <input
                  type="text"
                  value={formData.nit}
                  onChange={(e) => setFormData({ ...formData, nit: e.target.value })}
                  required
                  placeholder="123456789-1"
                />
              </div>
              <div className="form-group">
                <label>Tipo *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="cliente">Cliente</option>
                  <option value="proveedor">Proveedor</option>
                  <option value="empleado">Empleado</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Nombre / Razón Social *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Ciudad</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Departamento</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isLoading}>
                {createMutation.isLoading ? 'Creando...' : 'Crear Tercero'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="third-parties-list">
        <h3>Lista de Terceros ({data?.pagination?.total || 0})</h3>
        {data?.thirdParties && data.thirdParties.length > 0 ? (
          <table className="third-parties-table">
            <thead>
              <tr>
                <th>NIT</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Ciudad</th>
                <th>Email</th>
                <th>Estado NIT</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.thirdParties.map(tp => (
                <tr key={tp.id}>
                  <td>{tp.nit}</td>
                  <td>{tp.name}</td>
                  <td>
                    <span className={`type-badge type-${tp.type}`}>
                      {tp.type}
                    </span>
                  </td>
                  <td>{tp.city || '-'}</td>
                  <td>{tp.email || '-'}</td>
                  <td>
                    {tp.nitValidated ? (
                      <span className="nit-valid">✓ Validado</span>
                    ) : (
                      <span className="nit-invalid">No validado</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => setSelectedThirdParty(tp)}
                      className="btn-link"
                    >
                      Ver
                    </button>
                    {!tp.nitValidated && (
                      <button
                        onClick={() => handleValidateNIT(tp.id)}
                        className="btn-link"
                        disabled={validateNITMutation.isLoading}
                      >
                        Validar NIT
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay terceros registrados</p>
          </div>
        )}
      </div>

      {selectedThirdParty && (
        <div className="modal-overlay" onClick={() => setSelectedThirdParty(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalle de Tercero</h3>
              <button onClick={() => setSelectedThirdParty(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-item">
                <strong>NIT:</strong> {selectedThirdParty.nit}
                {selectedThirdParty.nitValidated && (
                  <span className="nit-valid-badge">✓ Validado</span>
                )}
              </div>
              <div className="detail-item">
                <strong>Nombre:</strong> {selectedThirdParty.name}
              </div>
              <div className="detail-item">
                <strong>Tipo:</strong> {selectedThirdParty.type}
              </div>
              <div className="detail-item">
                <strong>Dirección:</strong> {selectedThirdParty.address || '-'}
              </div>
              <div className="detail-item">
                <strong>Ciudad:</strong> {selectedThirdParty.city || '-'}
              </div>
              <div className="detail-item">
                <strong>Departamento:</strong> {selectedThirdParty.department || '-'}
              </div>
              <div className="detail-item">
                <strong>Teléfono:</strong> {selectedThirdParty.phone || '-'}
              </div>
              <div className="detail-item">
                <strong>Email:</strong> {selectedThirdParty.email || '-'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThirdParties;

